<script setup lang="ts">
import { PropType, ref, watch } from 'vue';
import { IonSpinner, loadingController } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

import type { DownloadModalProps } from '@/shared/interfaces/download';
import { DownloadType } from '@/shared/interfaces/download';
import type { TenantConfig } from '@/shared/interfaces/tenant';
import useDownload from '@/shared/composables/useDownload';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import useCurrencies from '@/shared/composables/useCurrencies';
import { OverviewTransaction } from '@/shared/interfaces/wallet';

const props = defineProps({
	shouldLoad: { type: Boolean, required: true },
	transactionId: { type: String, required: true },
	encryptedRfidTag: { type: String },
	tenantConfig: { type: Object as PropType<TenantConfig>, required: true },
});

// Init
const { t } = useI18n();
const { getTransactionDownloadLink, getWalletTransaction } = useWallet();
const { isLoading, shouldLoad, walletTransaction } = getWalletTransaction(props.transactionId, props.encryptedRfidTag);
const { convertToCurrency, groupAndSumTransactionsByCurrency } = useUtils();
const { presentDownloadAvailableModal } = useDownload();
const { fiatCurrency } = useCurrencies();
const walletTransactionTotal = ref();
const walletTransactionTotalFiat = ref();

watch(
	() => walletTransaction.value,
	(newValue) => {
		if (newValue && newValue.overview) {
			if (newValue.transaction.type === 'top_up') {
				// For topups, calculate total by subtracting debit from credit transactions
				const transactions = newValue.overview.transactions || [];
				let creditTotal = 0;
				let debitTotal = 0;

				transactions.forEach((transaction: OverviewTransaction) => {
					if (transaction.type === 'top_up_cost') {
						return;
					}

					const amount = parseFloat(transaction.amount);
					if (transaction.direction === 'CREDIT') {
						creditTotal += amount;
					} else if (transaction.direction === 'DEBIT') {
						debitTotal += amount;
					}
				});

				walletTransactionTotal.value = creditTotal - debitTotal;
				walletTransactionTotalFiat.value = parseFloat(newValue.overview.price);
			} else {
				// Check if this is a split payment
				if (newValue.transaction.is_split_payment) {
					// For split payments, find the RFID/wallet payment amount from the split payment metadata
					let walletAmount = 0;
					if (newValue.transaction.split_payment && newValue.transaction.split_payment.payment_methods) {
						const rfidMethod = newValue.transaction.split_payment.payment_methods.find((method) => method.type === 'rfid' && method.wallet_id === newValue.transaction.wallet_id);
						if (rfidMethod && rfidMethod.amount) {
							walletAmount = parseFloat(rfidMethod.amount);
						}
					}

					// Fallback to transaction amount if we can't find it in metadata
					if (walletAmount === 0) {
						walletAmount = Math.abs(parseFloat(newValue.transaction.amount));
					}

					walletTransactionTotal.value = walletAmount;
					walletTransactionTotalFiat.value = walletAmount * (newValue.transaction.currency?.exchange_rate ?? 1);
				} else {
					// The overview.price is in fiat currency, convert to token currency
					walletTransactionTotal.value = parseFloat(newValue.overview.price) / (newValue.transaction.currency?.exchange_rate ?? 1);
					walletTransactionTotalFiat.value = parseFloat(newValue.overview.price);
				}
			}
		}
	},
	{ immediate: true },
);

const onDownload = async () => {
	const loader = await loadingController.create({
		message: t('general.please_wait'),
	});
	await loader.present();

	const response = await getTransactionDownloadLink(walletTransaction.value!.transaction.id);

	const data: DownloadModalProps = {
		downloadItem: {
			href: response.download_link,
			linkName: t('download_modal.receipt_link_name', { reference: walletTransaction.value!.transaction.reference }),
		},
		title: t('download_modal.title', { file_name: t('download_modal.receipt_link_name', { reference: walletTransaction.value!.transaction.reference }) }),
		downloadType: DownloadType.INVOICE,
	};

	await presentDownloadAvailableModal(data);
	await loader.dismiss();
};

// Helper method to calculate the paid price for a 'top up cost' transaction
const paidTransactionCostPrice = (): number | string => {
	try {
		if (walletTransaction.value!.transaction.price_excl && walletTransaction.value!.transaction.price_vat) {
			return +walletTransaction.value!.transaction.price_excl + +walletTransaction.value!.transaction.price_vat;
		} else {
			return walletTransaction.value!.transaction.amount;
		}
	} catch (e) {
		return walletTransaction.value!.transaction.amount;
	}
};

const isCurrencyPrice = (transactionOverviewItem: any): boolean => {
	return transactionOverviewItem.is_currency_price && transactionOverviewItem.is_currency_price === true;
};

const paymentMethodTranslationKey = (): string => {
	let key = 'undefined';
	const translationKey = 'components.transaction_detail.';

	switch (walletTransaction.value!.transaction.order?.payment_method) {
		case 'ideal':
			key = 'payment_method_ideal';
			break;
		case 'paypal':
			key = 'payment_method_paypal';
			break;
		case 'creditcard':
			key = 'payment_method_credit_card';
			break;
		case 'applepay':
			key = 'payment_method_applepay';
			break;
		case 'bancontact':
			key = 'payment_method_bancontact';
			break;
		case 'voucher':
			key = 'payment_method_voucher';
			break;
		case 'wire_transfer':
			key = 'payment_method_wire_transfer';
			break;
		case 'virtual_wallet_transaction':
			key = 'payment_method_virtual_wallet';
			break;
		case 'virtual_wallet_transaction_order_only':
			key = 'payment_method_virtual_wallet_order_only';
			break;
		case 'virtual_wallet_transaction_free_offer':
			key = 'payment_method_virtual_wallet_free_offer';
			break;
		case 'virtual_wallet_transaction_free_staff':
			key = 'payment_method_virtual_wallet_free_staff';
			break;
		case 'virtual_wallet_transaction_cash_payment':
			key = 'payment_method_virtual_wallet_cash_payment';
			break;
		case 'virtual_wallet_transaction_card_payment':
			key = 'payment_method_virtual_wallet_card_payment';
			break;
		case 'virtual_wallet_split_payment':
			key = 'payment_method_split_payment';
			break;
	}

	return translationKey + key;
};

// Watch props and check if shouldLoad should change the state of the query
// and load data
watch(props, () => {
	if (props.shouldLoad) {
		shouldLoad.value = props.shouldLoad;
	}
});
</script>

<template>
	<div class="flex p-4 items-center justify-center" v-if="isLoading">
		<ion-spinner name="dots"></ion-spinner>
	</div>
	<div v-if="walletTransaction && walletTransaction.overview">
		<!-- DETAILS -->
		<div class="p-4 bg-white/10">
			<div class="flex justify-between">
				<span class="font-sourcesanspro-semibold text-l2 uppercase">
					{{ walletTransaction?.overview.type !== 'topup' ? t('transaction_overview.receipt') : t('transaction_overview.proof_of_transaction') }}
				</span>
				<span class="font-sourcesanspro-regular text-xs uppercase font-color-text-50">
					{{ t('components.transaction.pos_id') }}
					{{ walletTransaction?.transaction.scanned_by_id || t('components.transaction.none') }}
				</span>
			</div>
			<div class="flex justify-end">
				<span class="font-sourcesanspro-regular text-xs uppercase font-color-text-50">
					{{ t('components.transaction.id') }}
					{{ walletTransaction?.transaction.order?.nanoid || t('components.transaction.none') }}</span
				>
			</div>
			<div v-if="walletTransaction?.transaction.order?.location_name" class="flex justify-end">
				<span class="font-sourcesanspro-regular text-xs uppercase font-color-text-50">
					{{ t('components.transaction.location_name') }}
					{{ walletTransaction?.transaction.order?.location_name }}</span
				>
			</div>
			<div class="font-sourcesanspro-semibold text-sm2">{{ walletTransaction?.overview.invoicing.company }}</div>
			<div class="font-sourcesanspro-regular text-sm font-color-text-50">
				<div v-if="walletTransaction?.overview.invoicing.vat_id">{{ t('components.transaction.vat') }}: {{ walletTransaction?.overview.invoicing.vat_id }}</div>
				<div v-if="walletTransaction?.overview.invoicing.address.street && walletTransaction?.overview.invoicing.address.number && walletTransaction?.overview.invoicing.address.postal_code && walletTransaction?.overview.invoicing.address.city" class="mb-2">
					{{ walletTransaction?.overview.invoicing.address.street }}
					{{ walletTransaction?.overview.invoicing.address.number }},
					{{ walletTransaction?.overview.invoicing.address.postal_code }}
					{{ walletTransaction?.overview.invoicing.address.city }}
				</div>
			</div>

			<!-- TRANSACTION OVERVIEW DETAIL -->

			<!-- If transaction has another type, we will loop over all items and show the correct product line -->
			<div v-if="walletTransaction?.transaction.type !== 'top_up_cost'" class="mb-2">
				<div v-for="(transactionOverviewItem, index) of walletTransaction?.overview.items" :key="index" class="flex justify-between">
					<div class="font-sourcesanspro-regular text-sm font-color-text-50">
						{{ transactionOverviewItem.quantity }}x
						{{ transactionOverviewItem.label }}
					</div>
					<div v-if="transactionOverviewItem.price !== null" class="font-sourcesanspro-semibold text-sm2">
						<span>
							{{ isCurrencyPrice(transactionOverviewItem) || walletTransaction?.transaction.type === 'top_up' ? transactionOverviewItem.currency?.prefix : fiatCurrency.prefix }}
							{{ walletTransaction?.transaction.type === 'top_up' ? convertToCurrency((transactionOverviewItem.quantity * +transactionOverviewItem.price) / (transactionOverviewItem.currency?.exchange_rate || 1)) : convertToCurrency(transactionOverviewItem.quantity * +transactionOverviewItem.price) }}
							{{ walletTransaction?.transaction.type === 'top_up' ? transactionOverviewItem.currency?.suffix : '' }}
						</span>
						<span class="ml-5" v-if="transactionOverviewItem.vat_percent"> {{ transactionOverviewItem.vat_percent }}% </span>
					</div>
				</div>
			</div>

			<!-- If transaction is top up cost show transactionData instead of looping all transactionOverview lines -->
			<div v-if="walletTransaction?.transaction.type === 'top_up_cost'" class="mb-2">
				<div v-for="(transactionOverviewItem, index) of walletTransaction?.overview.items" :key="index" class="flex justify-between">
					<div class="font-sourcesanspro-regular text-sm font-color-text-50">
						1x {{ t('components.transaction.type.top_up_cost') }}
						<span> ({{ fiatCurrency.prefix }} {{ convertToCurrency(+walletTransaction?.transaction?.amount) }}) </span>
					</div>
					<div v-if="transactionOverviewItem.price !== null" class="font-sourcesanspro-semibold text-sm2">
						<span class="value tx-value" v-if="walletTransaction?.transaction?.vat_percentage"> {{ walletTransaction?.transaction.vat_percentage }}% </span>
					</div>
				</div>
			</div>

			<!-- TRANSACTION VAT OVERVIEW DETAIL -->
			<div v-if="walletTransaction?.overview && walletTransaction?.overview.vat_items.length > 0">
				<div v-for="(transactionVatOverviewItem, index) of walletTransaction?.overview.vat_items" :key="index" class="flex justify-between">
					<div class="font-sourcesanspro-regular text-sm font-color-text-50">
						{{ t('components.transaction.vat') }}
						{{ transactionVatOverviewItem.label }}

						<span> ({{ transactionVatOverviewItem?.currency?.prefix ?? fiatCurrency.prefix }} {{ convertToCurrency(transactionVatOverviewItem.price) }}) </span>
					</div>
				</div>
			</div>
		</div>

		<hr class="h-px bg-white/20 border-0" />

		<!-- TOTAL DETAILS -->
		<div class="p-4 bg-white/10">
			<!-- TRANSACTION TYPES AND TOTALS -->
			<div v-if="walletTransaction?.transaction.type !== 'top_up_cost'">
				<div
					v-for="(transactionGroup, index) in groupAndSumTransactionsByCurrency(
						// For split payments, only show transactions for this wallet
						walletTransaction?.transaction.is_split_payment ? (walletTransaction?.overview.transactions || []).filter((t) => t.wallet_id === walletTransaction.transaction.wallet_id) : walletTransaction?.overview.transactions,
					)"
					:key="index"
					class="flex justify-between">
					<span v-if="transactionGroup.type === 'payment'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction_detail.transaction_types.payment') }}
					</span>
					<span v-if="transactionGroup.type === 'refund_transaction_cost'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction_detail.transaction_types.refund_transaction_cost') }}
					</span>
					<span v-if="transactionGroup.type === 'top_up'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction.type.top_up') }}
					</span>
					<span v-if="transactionGroup.type === 'refund_holding'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction.type.refund_holding') }}
					</span>
					<span v-if="transactionGroup.type === 'event_ticket_purchase'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction.type.event_ticket_purchase') }}
					</span>
					<span v-if="transactionGroup.type === 'warranty_products_return'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction.type.warranty_products_return') }}
					</span>
					<span v-if="transactionGroup.type === 'products_purchased'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction.type.products_purchased') }}
					</span>
					<span v-if="transactionGroup.type === 'web_shop_products_purchased'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction.type.web_shop_products_purchased') }}
					</span>
					<span v-if="transactionGroup.type === 'rfid_card_creation_cost'" class="font-sourcesanspro-regular text-sm2 font-color-text-50"> {{ t('wallet.rfid_activation_cost') }} </span>
					<span v-if="transactionGroup.type === 'refunded_amount'" class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction_detail.transaction_types.refund') }}
					</span>

					<!--					<span-->
					<!--						v-if="transactionGroup.type === 'top_up_cost'"-->
					<!--						class="font-sourcesanspro-regular text-sm2 font-color-text-50">-->
					<!--						{{ t('components.transaction_detail.transaction_types.top_up_cost') }}-->
					<!--					</span>-->
					<div v-if="transactionGroup.type !== 'top_up_cost'" class="font-sourcesanspro-semibold text-sm2 text-nowrap">
						<span>{{ transactionGroup?.prefix }} {{ convertToCurrency(+transactionGroup?.totalAmount) }}</span> <small v-if="transactionGroup.totalFiatAmount !== transactionGroup.totalAmount">(&euro; {{ convertToCurrency(+transactionGroup.totalFiatAmount) }})</small>
					</div>

					<!--					<div v-if="transactionGroup.type === 'top_up_cost'">-->
					<!--						<div class="font-sourcesanspro-semibold text-sm2 text-nowrap">-->
					<!--							<span>-->
					<!--								{{ fiatCurrency.prefix }} {{ convertToCurrency(+transactionGroup.totalAmount) }}-->
					<!--							</span>-->
					<!--						</div>-->
					<!--					</div>-->
				</div>
			</div>
			<!--
        IF the transaction in overview has the type top_up_cost,
        We should hide the amount and show:
          - Amount without VAT
          - Amount VAT
      -->
			<div v-else>
				<div class="flex justify-between">
					<span class="font-sourcesanspro-regular text-sm2 font-color-text-50">
						{{ t('components.transaction_detail.transaction_types.top_up_cost') }}
					</span>
					<div class="font-sourcesanspro-semibold text-sm2">
						<span v-if="walletTransaction.transaction.type === 'top_up_cost'"> - {{ fiatCurrency.prefix }} {{ convertToCurrency(+paidTransactionCostPrice()) }} </span>
						<span v-if="walletTransaction.transaction.type !== 'top_up_cost'"> {{ walletTransaction.transaction.currency.prefix }} {{ convertToCurrency(paidTransactionCostPrice()) }} </span>
					</div>
				</div>
			</div>

			<div class="flex justify-between">
				<span class="font-sourcesanspro-regular text-sm2 font-color-text-50 font-bold">{{ t('components.transaction_detail.total_label') }}</span>

				<!-- Total of transaction cost -->
				<div v-if="walletTransaction?.transaction.type === 'top_up'">
					<span class="font-sourcesanspro-semibold text-sm2"> {{ fiatCurrency.prefix }} {{ convertToCurrency(+walletTransactionTotalFiat) }} </span>
				</div>
				<!-- If transaction is a 'top up cost' we will show the correct amount -->
				<div v-if="walletTransaction?.transaction.type !== 'top_up'">
					<span v-if="walletTransaction?.transaction.type !== 'top_up_cost'" class="font-sourcesanspro-semibold text-sm2">
						<span v-if="walletTransaction?.transaction?.order"
							>{{ walletTransaction?.transaction?.currency?.prefix }}
							{{ convertToCurrency(walletTransaction.transaction.direction === 'DEBIT' ? +walletTransactionTotal * -1 : +walletTransactionTotal) }}
							<small v-if="walletTransaction?.transaction?.currency?.exchange_rate !== 1"> ({{ fiatCurrency.prefix }}{{ convertToCurrency(walletTransaction.transaction.direction === 'DEBIT' ? +walletTransactionTotalFiat * -1 : +walletTransactionTotalFiat) }}) </small></span
						>
						<span v-else>{{ fiatCurrency.prefix }}{{ convertToCurrency(+walletTransactionTotalFiat) }}</span>
					</span>
				</div>

				<span v-if="walletTransaction?.transaction.type === 'top_up_cost'" class="font-sourcesanspro-semibold text-sm2"> - {{ fiatCurrency.prefix }} {{ convertToCurrency(+walletTransaction?.transaction.amount) }} </span>
			</div>

			<!-- Split Payment Details -->
			<div v-if="walletTransaction?.transaction.is_split_payment" class="mt-5 p-3 bg-white/5 rounded">
				<div class="flex justify-between mb-2">
					<span class="font-sourcesanspro-semibold text-sm2 text-orange-400">
						{{ t('components.transaction_detail.split_payment_details') }}
					</span>
				</div>
				<div class="flex justify-between mb-1">
					<span class="font-sourcesanspro-regular text-sm font-color-text-50">
						{{ t('components.transaction_detail.total_order_amount') }}
					</span>
					<span class="font-sourcesanspro-semibold text-sm">
						{{ walletTransaction?.transaction?.currency?.prefix }} {{ convertToCurrency(+walletTransaction.transaction.total_order_price / (walletTransaction?.transaction?.currency?.exchange_rate ?? 1)) }}
						<small v-if="walletTransaction?.transaction?.currency?.exchange_rate !== 1"> ({{ fiatCurrency.prefix }} {{ convertToCurrency(+walletTransaction.transaction.total_order_price) }}) </small>
					</span>
				</div>
				<div class="flex justify-between mb-1">
					<span class="font-sourcesanspro-regular text-sm font-color-text-50">
						{{ t('components.transaction_detail.wallet_portion') }}
					</span>
					<span class="font-sourcesanspro-semibold text-sm">
						{{ walletTransaction?.transaction?.currency?.prefix }} {{ convertToCurrency(Math.abs(+walletTransactionTotal)) }}
						<small v-if="walletTransaction?.transaction?.currency?.exchange_rate !== 1"> ({{ fiatCurrency.prefix }}{{ convertToCurrency(Math.abs(+walletTransactionTotalFiat)) }}) </small>
					</span>
				</div>
				<div v-if="walletTransaction.transaction.split_payment" class="mt-3 pt-3 border-t border-white/10">
					<div class="text-xs font-color-text-50 mb-2 uppercase font-sourcesanspro-semibold">
						{{ t('components.transaction_detail.payment_methods_used') }}
					</div>
					<div v-for="(method, index) in walletTransaction.transaction.split_payment.payment_methods" :key="`method-${index}`" class="flex justify-between items-center mb-1">
						<div class="flex items-center">
							<div class="w-2 h-2 rounded-full mr-2" :class="method.type === 'rfid' ? 'bg-orange-400' : 'bg-green-400'"></div>
							<span class="font-sourcesanspro-regular text-xs font-color-text-50 capitalize">
								{{ method.type === 'rfid' ? t('components.transaction_detail.payment_method_rfid') : t('components.transaction_detail.payment_method_' + method.type) }}
							</span>
						</div>
						<span class="text-xs font-color-text-50">
							<span v-if="method.amount"> {{ method.type === 'rfid' ? walletTransaction?.transaction?.currency?.prefix : fiatCurrency.prefix }} {{ method.amount_formatted || convertToCurrency(method.amount) }} </span>
							<span v-else-if="method.wallet_id"> Wallet {{ method.wallet_id }} </span>
						</span>
					</div>
				</div>
			</div>

			<!-- Refund Holding Details -->
			<div v-if="walletTransaction?.transaction.type === 'refund_holding' && walletTransaction?.transaction.refund_request" class="mt-5 p-3 bg-white/5 rounded">
				<div class="flex justify-between mb-2">
					<span class="font-sourcesanspro-semibold text-sm2 text-orange-400">
						{{ t('components.transaction_detail.refund_details') }}
					</span>
				</div>
				<div class="flex justify-between mb-1">
					<span class="font-sourcesanspro-regular text-sm font-color-text-50">
						{{ t('components.transaction_detail.total_requested') }}
					</span>
					<span class="font-sourcesanspro-semibold text-sm"> {{ fiatCurrency.prefix }} {{ convertToCurrency(Math.abs(+walletTransaction.transaction.refund_request.fiat_amount)) }} </span>
				</div>
				<div v-if="walletTransaction.transaction.refund_request.include_transaction_cost && walletTransaction.transaction.refund_request.transaction_cost" class="flex justify-between mb-1">
					<span class="font-sourcesanspro-regular text-sm font-color-text-50">
						{{ t('components.transaction_detail.refund_cost') }}
					</span>
					<span class="font-sourcesanspro-semibold text-sm text-red-400"> - {{ fiatCurrency.prefix }} {{ convertToCurrency(+walletTransaction.transaction.refund_request.transaction_cost) }} </span>
				</div>
				<div class="flex justify-between pt-2 border-t border-white/10">
					<span class="font-sourcesanspro-semibold text-sm2 text-green-400">
						{{ t('components.transaction_detail.total_refunded') }}
					</span>
					<span class="font-sourcesanspro-semibold text-sm2 text-green-400"> {{ fiatCurrency.prefix }} {{ convertToCurrency(Math.abs(+walletTransaction.transaction.refund_request.fiat_amount) - (walletTransaction.transaction.refund_request.include_transaction_cost ? +walletTransaction.transaction.refund_request.transaction_cost || 0 : 0)) }} </span>
				</div>
			</div>

			<div class="flex justify-between mt-5">
				<span class="font-sourcesanspro-regular text-sm2 font-color-text-50">
					{{ t('components.transaction_detail.payment_method') }}
				</span>
				<span class="font-sourcesanspro-semibold text-sm2">
					<span v-if="walletTransaction?.transaction.is_split_payment"> Split Payment </span>
					<span v-else>
						{{ t(paymentMethodTranslationKey()) }}
					</span>
				</span>
			</div>
			<div v-if="walletTransaction?.overview?.payment_method === 'wire_transfer'">
				<span class="font-sourcesanspro-regular text-sm2 font-color-text-50">{{ t('components.transaction_detail.refund_wire_transfer_period') }}</span>
			</div>
			<div v-if="walletTransaction?.overview.status !== 'no-order'" class="flex justify-between">
				<span class="font-sourcesanspro-regular text-sm2 font-color-text-50">
					{{ t('components.transaction_detail.payment_status') }}
				</span>
				<span class="font-sourcesanspro-semibold text-sm2 capitalize">
					{{ walletTransaction?.overview.status }}
				</span>
			</div>
		</div>

		<!--		<div class="flex p-4 items-center justify-center bg-white/10">-->
		<!--			<ion-button class="mb-4" size="large" shape="round" @click="onDownload()">-->
		<!--				<div class="flex items-center justify-center">-->
		<!--					<ion-icon :icon="downloadOutline" class="text-dark-blue mr-2"></ion-icon>-->
		<!--					<span class="text-m2">Download</span>-->
		<!--				</div>-->
		<!--			</ion-button>-->
		<!--		</div>-->
	</div>
</template>

<style scoped></style>
